import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Heading, Stat } from "./NKDaily";

export default function Analytics() {
  const [data, setData] = useState(null);
  const [part, setPart] = useState(null);
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/participation?days=14").then((r) => setPart(r.data)).catch(() => {});
    api.get("/visits/admin/analytics/visits?days=14").then((r) => setVisits(r.data)).catch(() => {});
  }, []);

  if (!data) return <><Heading title="Analytics" /><p className="text-nk-mute text-sm">Loading…</p></>;
  const g = data.growth || {};
  const gp = data.gameplay || {};
  const r = data.revenue || {};
  const vt = visits?.totals || {};

  return (
    <div className="space-y-8">
      <Heading title="Analytics" subtitle="The page investors will love. Growth, engagement, revenue." />

      <section>
        <p className="eyebrow mb-3">Visits · last 14 days</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <Stat label="Total unique visitors" value={(vt.total_unique_visitors ?? 0).toLocaleString()} />
          <Stat label="New visitors (14d)" value={(vt.new_visitors_in_window ?? 0).toLocaleString()} />
          <Stat label="Page loads (14d)" value={(vt.page_loads_in_window ?? 0).toLocaleString()} />
        </div>
        {visits && visits.days.length > 0 ? (
          <div className="nk-card overflow-x-auto" data-testid="admin-visits-table">
            <table className="w-full text-xs min-w-[480px]">
              <thead>
                <tr className="border-b border-nk-line text-left text-nk-mute">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-3 py-3 font-medium text-right">New visits</th>
                  <th className="px-3 py-3 font-medium text-right">Returning</th>
                  <th className="px-4 py-3 font-medium text-right text-nk-accent">Page loads</th>
                </tr>
              </thead>
              <tbody>
                {visits.days.map((d) => (
                  <tr key={d.date} className="border-b border-nk-line/40 last:border-0">
                    <td className="px-4 py-2.5 font-mono text-nk-mute">{d.date}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-nk-accent">{d.new_visits}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{d.returning_visits}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{d.total_events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="nk-card p-6 text-sm text-nk-mute">No visits recorded yet. Open the site in a fresh browser to test.</div>
        )}
      </section>

      <section>
        <p className="eyebrow mb-3">Growth</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total players" value={(g.total_players ?? 0).toLocaleString()} />
          <Stat label="DAU" value={(g.dau ?? 0).toLocaleString()} />
          <Stat label="WAU" value={(g.wau ?? 0).toLocaleString()} />
          <Stat label="MAU" value={(g.mau ?? 0).toLocaleString()} />
          <Stat label="Stickiness (DAU/MAU)" value={`${g.stickiness_pct ?? 0}%`} />
        </div>
      </section>

      <section>
        <p className="eyebrow mb-3">Gameplay · all-time</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="NK Daily plays" value={(gp.daily_plays ?? 0).toLocaleString()} />
          <Stat label="Ladder plays" value={(gp.ladder_plays ?? 0).toLocaleString()} />
          <Stat label="Duels resolved" value={(gp.duel_plays ?? 0).toLocaleString()} />
          <Stat label="Survival runs" value={(gp.survival_plays ?? 0).toLocaleString()} />
        </div>
      </section>

      <section>
        <p className="eyebrow mb-3">Daily participation · last 14 days</p>
        <p className="text-[11px] text-nk-dim mb-3">Unique players who earned Season Score, per mode, per day (UTC).</p>
        {!part || part.days.length === 0 ? (
          <div className="nk-card p-6 text-sm text-nk-mute">No participation recorded in this window yet.</div>
        ) : (
          <div className="nk-card overflow-x-auto" data-testid="admin-participation-table">
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="border-b border-nk-line text-left text-nk-mute">
                  <th className="px-4 py-3 font-medium">Date</th>
                  {part.modes.map((m) => (
                    <th key={m} className="px-3 py-3 font-medium capitalize text-right">{m}</th>
                  ))}
                  <th className="px-4 py-3 font-medium text-right text-nk-accent">Unique players</th>
                </tr>
              </thead>
              <tbody>
                {part.days.map((d) => (
                  <tr key={d.date} className="border-b border-nk-line/40 last:border-0">
                    <td className="px-4 py-2.5 font-mono text-nk-mute">{d.date}</td>
                    {part.modes.map((m) => {
                      const v = d.modes[m];
                      return (
                        <td key={m} className="px-3 py-2.5 text-right font-mono">
                          {v ? <span className="text-nk-text">{v.players}</span> : <span className="text-nk-dim/50">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-right font-mono text-nk-accent font-medium">{d.total_players}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <p className="eyebrow mb-3">Revenue</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="NK+ subscribers" value={(r.nk_plus_subscribers ?? 0).toLocaleString()} />
          <Stat label="Est. NK+ MRR (USD)" value={`$${(r.nk_plus_monthly_revenue_estimate_usd ?? 0).toFixed(2)}`} />
          <Stat label="Active sponsors" value={r.sponsors_active ?? 0} />
        </div>
        <p className="text-[11px] text-nk-dim mt-3">
          Revenue numbers are estimates based on a $5/mo NK+ default. Real Paystack/Stripe attribution wires in once payments go live.
        </p>
      </section>
    </div>
  );
}
